import jobBoardAPI from './base';

export interface SharePointFile {
  id: string;
  name: string;
  size: number;
  lastModifiedDateTime: string;
  webUrl: string;
  fileType: string;
  createdBy: {
    user: {
      displayName: string;
      email: string;
      id: string;
    };
  };
  thumbnailUrl?: string;
  downloadUrl?: string;
}

export interface SharePointFolder {
  id: string;
  name: string;
  lastModifiedDateTime: string;
  webUrl: string;
  createdBy: {
    user: {
      displayName: string;
      email: string;
      id: string;
    };
  };
  childCount: number;
  path?: string;
}

export interface SharePointCompanyData {
  folders: SharePointFolder[];
  files: SharePointFile[];
}

export interface SharePointFolderDetails {
  folders: SharePointFolder[];
  files: SharePointFile[];
  currentFolder: SharePointFolder;
}

export interface SharePointJobData {
  [companyName: string]: {
    [jobNumber: string]: {
      folders: SharePointFolder[];
      files: SharePointFile[];
    };
  };
}

export interface JobResult {
  jobNumber: string;
  companyName: string;
  folders: SharePointFolder[];
  files: SharePointFile[];
  found: boolean;
  path: string;
}

export interface SharePointJobResponse {
  results: JobResult[];
  summary: {
    totalJobNumbers: number;
    foundJobNumbers: number;
    notFoundJobNumbers: string[];
  };
}

export interface SharePointUploadResponse {
  id: string;
  name: string;
  webUrl: string;
  size: number;
  fileType: string;
  uploadDateTime: string;
}

export interface SharePointFolderCreateResponse {
  id: string;
  name: string;
  webUrl: string;
}

export const SHAREPOINT_ENDPOINTS = {
  GET_JOB_FOLDERS: '/sharepoint/sharepoint/companies/job-folders',
  GET_FOLDER_DETAILS: '/sharepoint/sharepoint/folder/',
  UPLOAD_FILE: '/sharepoint/sharepoint/upload',
  UPLOAD_IMAGE: '/sharepoint/sharepoint/upload-image',
  CREATE_FOLDER: '/sharepoint/sharepoint/folder'
} as const;

export const SHAREPOINT_API = {
  GET_JOB_FOLDERS: async (jobNumbers: string[], companyNames?: string[]) => {
    const trimmedCompanyNames = companyNames
      ?.map(name => name.trim())
      .filter(name => name.length > 0);

    const response = await jobBoardAPI.post<{ data: SharePointJobResponse }>(
      SHAREPOINT_ENDPOINTS.GET_JOB_FOLDERS,
      { jobNumbers, companyNames: trimmedCompanyNames }
    );
    return response.data.data;
  },

  GET_FOLDER_DETAILS: async (folderId: string) => {
    const response = await jobBoardAPI.get<{ data: SharePointFolderDetails }>(
      `${SHAREPOINT_ENDPOINTS.GET_FOLDER_DETAILS}${folderId}`
    );
    return response.data.data;
  },

  UPLOAD_FILE: async (folderId: string, file: File, fileName?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folderId', folderId);
    if (fileName) {
      formData.append('fileName', fileName);
    }

    const response = await jobBoardAPI.post<{ data: SharePointUploadResponse }>(
      SHAREPOINT_ENDPOINTS.UPLOAD_FILE,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data.data;
  },

  UPLOAD_IMAGE: async (folderId: string, file: File, fileName?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folderId', folderId);
    if (fileName) {
      formData.append('fileName', fileName);
    }

    const response = await jobBoardAPI.post<{ data: SharePointUploadResponse }>(
      SHAREPOINT_ENDPOINTS.UPLOAD_IMAGE,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data.data;
  },

  CREATE_FOLDER: async (parentFolderId: string, folderName: string) => {
    const response = await jobBoardAPI.post<{
      data: SharePointFolderCreateResponse;
    }>(SHAREPOINT_ENDPOINTS.CREATE_FOLDER, { parentFolderId, folderName });
    return response.data.data;
  }
};

// Convenience functions
export const uploadFileToSharePoint = async (
  folderId: string,
  file: File
): Promise<SharePointUploadResponse> => {
  return SHAREPOINT_API.UPLOAD_FILE(folderId, file);
};

export const uploadImageToSharePoint = async (
  folderId: string,
  file: File
): Promise<SharePointUploadResponse> => {
  return SHAREPOINT_API.UPLOAD_IMAGE(folderId, file);
};

export const createSharePointFolder = async (
  parentFolderId: string,
  folderName: string
): Promise<SharePointFolderCreateResponse> => {
  return SHAREPOINT_API.CREATE_FOLDER(parentFolderId, folderName);
};

export const getJobFolderId = async (
  jobNumbers: string[],
  companyNames: string[]
): Promise<{ results: JobResult[] }> => {
  const response = await SHAREPOINT_API.GET_JOB_FOLDERS(
    jobNumbers,
    companyNames
  );
  return { results: response.results };
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const getFileIcon = (fileType: string): string => {
  const iconMap: { [key: string]: string } = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      'fa-file-excel',
    'application/vnd.ms-excel': 'fa-file-excel',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      'fa-file-word',
    'application/pdf': 'fa-file-pdf',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      'fa-file-powerpoint',
    'text/plain': 'fa-file-text',
    'image/jpeg': 'fa-file-image',
    'image/png': 'fa-file-image',
    'image/gif': 'fa-file-image',
    'application/zip': 'fa-file-archive',
    'application/x-rar-compressed': 'fa-file-archive'
  };

  return iconMap[fileType] || 'fa-file';
};

export const isFilePreviewable = (fileType: string): boolean => {
  const previewableTypes = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'image/jpeg',
    'image/png',
    'image/gif'
  ];
  return previewableTypes.includes(fileType);
};

export const openFilePreview = async (file: SharePointFile) => {
  try {
    if (!file || !file.webUrl) {
      throw new Error('Invalid file data provided');
    }

    window.open(file.webUrl, '_blank');
  } catch (error) {
    console.error('Failed to open file preview:', error);

    try {
      const fallbackUrl = `https://holeopener.sharepoint.com/_layouts/15/Doc.aspx?sourcedoc={${file.id}}&action=default`;
      window.open(fallbackUrl, '_blank');
    } catch (fallbackError) {
      console.error('Fallback preview also failed:', fallbackError);
      throw new Error(
        'Unable to preview file. Please try downloading instead.'
      );
    }
  }
};

export const downloadSharePointFile = async (file: SharePointFile) => {
  try {
    if (file.downloadUrl) {
      window.open(file.downloadUrl, '_blank');
      return;
    }

    if (file.webUrl) {
      const downloadUrl = `${file.webUrl}?download=1`;
      window.open(downloadUrl, '_blank');
      return;
    }

    const fallbackDownloadUrl = `https://holeopener.sharepoint.com/_layouts/15/download.aspx?SourceUrl=${encodeURIComponent(
      file.webUrl || ''
    )}&UniqueId=${file.id}`;
    window.open(fallbackDownloadUrl, '_blank');
  } catch (error) {
    console.error('Failed to download file:', error);
    throw error;
  }
};

/**
 * Organize job results by company for better display
 */
export const organizeJobResultsByCompany = (
  results: JobResult[]
): {
  [companyName: string]: {
    jobs: JobResult[];
    totalFolders: number;
    totalFiles: number;
  };
} => {
  const organized: {
    [companyName: string]: {
      jobs: JobResult[];
      totalFolders: number;
      totalFiles: number;
    };
  } = {};

  results?.forEach(result => {
    if (!organized[result.companyName]) {
      organized[result.companyName] = {
        jobs: [],
        totalFolders: 0,
        totalFiles: 0
      };
    }

    organized[result.companyName].jobs.push(result);
    organized[result.companyName].totalFolders += result.folders.length;
    organized[result.companyName].totalFiles += result.files.length;
  });

  return organized;
};

/**
 * Get all folders from job results
 */
export const getAllFoldersFromResults = (
  results: JobResult[]
): SharePointFolder[] => {
  const allFolders: SharePointFolder[] = [];

  results?.forEach(result => {
    if (result.found) {
      allFolders.push(...result.folders);
    }
  });

  return allFolders;
};

/**
 * Get all files from job results
 */
export const getAllFilesFromResults = (
  results: JobResult[]
): SharePointFile[] => {
  const allFiles: SharePointFile[] = [];

  results?.forEach(result => {
    if (result.found) {
      allFiles.push(...result.files);
    }
  });

  return allFiles;
};

/**
 * Filter results by found status
 */
export const getFoundJobResults = (results: JobResult[]): JobResult[] => {
  return results?.filter(result => result?.found) || [];
};

/**
 * Get job numbers that were not found
 */
export const getNotFoundJobNumbers = (results: JobResult[]): string[] => {
  return (
    results?.filter(result => !result.found)?.map(result => result.jobNumber) ||
    []
  );
};
